import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Camera, Upload, FileText, Image as ImageIcon, ChevronLeft, Loader2,
  Plus, Trash2, RefreshCw, ArrowUp, ArrowDown, CheckCircle2, X,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { BottomNav } from "@/components/BottomNav";
import { DocDeleteButton } from "@/components/DocDeleteButton";

const MAX_PAGES = 15;
const STORE_KEY = "esvan_capture_pages";

function fmtSize(bytes) {
  if (!bytes) return "";
  const kb = bytes / 1024;
  return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
}

const MAX_DIM = 1600;

// Downscale photos client-side (keeps OCR quality) so multi-page sessions fit in sessionStorage
// and uploads stay small. PDFs are passed through unchanged.
const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    if (!file.type || !file.type.startsWith("image/")) {
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(file);
      return;
    }
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, MAX_DIM / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.72));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

async function dataUrlToFile(dataUrl, name, type) {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], name, { type: type || blob.type });
}

export default function NewDocument() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const replaceIndexRef = useRef(null);
  const [pages, setPages] = useState([]); // [{id, name, type, dataUrl}]
  const [uploading, setUploading] = useState(false);

  // Restore an interrupted capture session (survives reload / returning from the camera).
  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORE_KEY));
      if (Array.isArray(saved) && saved.length) setPages(saved);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(pages)); } catch (e) { /* quota */ }
  }, [pages]);

  const { data: docs = [] } = useQuery({
    queryKey: ["documents"],
    queryFn: async () => (await api.get("/documents")).data,
  });

  const onPick = async (file) => {
    if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    const mime = dataUrl.slice(5, dataUrl.indexOf(";")) || file.type || "image/jpeg";
    const entry = { id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, name: file.name || "page", type: mime, dataUrl };
    const rIdx = replaceIndexRef.current;
    if (rIdx !== null && rIdx !== undefined) {
      setPages((prev) => prev.map((p, i) => (i === rIdx ? entry : p)));
      replaceIndexRef.current = null;
    } else {
      setPages((prev) => (prev.length >= MAX_PAGES ? prev : [...prev, entry]));
    }
    if (cameraRef.current) cameraRef.current.value = "";
    if (fileRef.current) fileRef.current.value = "";
  };

  const addFromCamera = () => { replaceIndexRef.current = null; cameraRef.current?.click(); };
  const addFromFile = () => { replaceIndexRef.current = null; fileRef.current?.click(); };
  const replacePage = (i) => { replaceIndexRef.current = i; cameraRef.current?.click(); };
  const removePage = (i) => setPages((prev) => prev.filter((_, idx) => idx !== i));
  const move = (i, dir) =>
    setPages((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const done = async () => {
    if (!pages.length || uploading) return;
    setUploading(true);
    try {
      const form = new FormData();
      for (let i = 0; i < pages.length; i++) {
        const p = pages[i];
        const f = await dataUrlToFile(p.dataUrl, p.name || `page-${i + 1}.png`, p.type);
        form.append("files", f);
      }
      form.append("language", lang);
      const { data } = await api.post("/documents/multipage", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      sessionStorage.removeItem(STORE_KEY);
      setPages([]);
      qc.invalidateQueries({ queryKey: ["documents"] });
      toast.success(t("doc.uploaded"));
      navigate(`/documents/${data.document.id}/analysis`);
    } catch (err) {
      toast.error(apiError(err, t));
      setUploading(false);
    }
  };

  return (
    <div className="relative mx-auto min-h-screen max-w-md border-x border-[#E2EAE5]/60 bg-[#F8FAF8] pb-28 shadow-sm">
      <header className="sticky top-0 z-40 flex items-center gap-2 border-b border-[#E2EAE5] bg-[#F8FAF8]/90 px-4 py-3 backdrop-blur-md">
        <button data-testid="doc-back-button" onClick={() => navigate(-1)} className="rounded-full p-1.5 text-[#4A6358] hover:bg-[#F0F4F1]">
          <ChevronLeft size={22} />
        </button>
        <h1 className="font-display text-base font-bold text-[#1A2E26]">{t("doc.title")}</h1>
      </header>

      <main className="esv-fade px-4 pt-5">
        <p className="mb-5 text-sm text-[#7A9488]">{t("doc.subtitle")}</p>

        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
          data-testid="doc-camera-input" onChange={(e) => onPick(e.target.files?.[0])} />
        <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
          data-testid="doc-file-input" onChange={(e) => onPick(e.target.files?.[0])} />

        <div className="grid grid-cols-2 gap-3">
          <button
            data-testid="doc-take-photo-button"
            disabled={uploading || pages.length >= MAX_PAGES}
            onClick={addFromCamera}
            className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-[#E2EAE5] bg-white py-8 text-[#2D5A4C] transition-all hover:border-[#C4DFD2] hover:shadow-sm active:scale-[0.98] disabled:opacity-50"
          >
            <Camera size={28} />
            <span className="text-xs font-semibold">{t("doc.takePhoto")}</span>
          </button>
          <button
            data-testid="doc-choose-file-button"
            disabled={uploading || pages.length >= MAX_PAGES}
            onClick={addFromFile}
            className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-[#E2EAE5] bg-white py-8 text-[#2D5A4C] transition-all hover:border-[#C4DFD2] hover:shadow-sm active:scale-[0.98] disabled:opacity-50"
          >
            <Upload size={28} />
            <span className="text-xs font-semibold">{t("doc.choosePdf")}</span>
          </button>
        </div>

        {/* Capture session — pages held locally until "Done" */}
        <div className="mt-5" data-testid="capture-session">
          {pages.length === 0 ? (
            <p className="rounded-xl border border-dashed border-[#C4DFD2] bg-[#F2F7F4] px-3 py-4 text-center text-xs text-[#7A9488]">
              {t("xp.mpEmpty")}
            </p>
          ) : (
            <div className="space-y-2">
              {pages.map((p, i) => (
                <div key={p.id} data-testid={`capture-page-${i}`} className="flex items-center gap-3 rounded-2xl border border-[#E2EAE5] bg-white p-2.5">
                  <a href={p.dataUrl} target="_blank" rel="noreferrer" data-testid={`capture-view-${i}`} className="shrink-0">
                    {p.type === "application/pdf" ? (
                      <div className="flex h-14 w-11 items-center justify-center rounded-lg bg-[#F0F4F1] text-[#2D5A4C]"><FileText size={20} /></div>
                    ) : (
                      <img src={p.dataUrl} alt={`page ${i + 1}`} className="h-14 w-11 rounded-lg border border-[#E2EAE5] object-cover" />
                    )}
                  </a>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#1A2E26]">{t("xp.mpPage").replace("{n}", i + 1)}</p>
                    <div className="mt-1 flex items-center gap-1">
                      <button aria-label="move up" data-testid={`capture-up-${i}`} disabled={i === 0} onClick={() => move(i, -1)} className="rounded-md p-1.5 text-[#4A6358] hover:bg-[#F0F4F1] disabled:opacity-30"><ArrowUp size={15} /></button>
                      <button aria-label="move down" data-testid={`capture-down-${i}`} disabled={i === pages.length - 1} onClick={() => move(i, 1)} className="rounded-md p-1.5 text-[#4A6358] hover:bg-[#F0F4F1] disabled:opacity-30"><ArrowDown size={15} /></button>
                      <button aria-label="replace" data-testid={`capture-replace-${i}`} onClick={() => replacePage(i)} className="rounded-md p-1.5 text-[#4A6358] hover:bg-[#F0F4F1]"><RefreshCw size={15} /></button>
                      <button aria-label="delete" data-testid={`capture-delete-${i}`} onClick={() => removePage(i)} className="rounded-md p-1.5 text-[#C03A2B] hover:bg-[#FDF2F0]"><Trash2 size={15} /></button>
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between px-1 pt-1">
                <button data-testid="capture-add-page" disabled={pages.length >= MAX_PAGES} onClick={addFromCamera} className="flex items-center gap-1.5 text-sm font-semibold text-[#2D5A4C] disabled:opacity-40">
                  <Plus size={16} /> {t("xp.mpAddPage")}
                </button>
                {pages.length >= MAX_PAGES && <span className="text-[11px] text-[#B36B00]">{t("xp.mpMax")}</span>}
              </div>
              <Button
                data-testid="capture-done"
                disabled={uploading}
                onClick={done}
                className="mt-1 h-12 w-full rounded-xl bg-[#2D5A4C] font-semibold hover:bg-[#214439]"
              >
                {uploading ? <><Loader2 size={16} className="mr-2 animate-spin" /> {t("doc.uploading")}</> : <><CheckCircle2 size={16} className="mr-2" /> {t("xp.mpDone")}</>}
              </Button>
            </div>
          )}
        </div>

        <div className="mt-8">
          <h2 className="mb-3 font-display text-sm font-bold uppercase tracking-wide text-[#4A6358]">
            {t("doc.myDocuments")}
          </h2>
          {docs.length === 0 ? (
            <p className="rounded-xl border border-[#E2EAE5] bg-white/60 px-3 py-4 text-center text-xs text-[#B0C2B8]">
              {t("doc.noDocuments")}
            </p>
          ) : (
            <div className="space-y-2">
              {docs.map((d) => {
                const isPdf = d.content_type === "application/pdf";
                const npages = d.page_count || 1;
                return (
                  <div
                    key={d.id}
                    data-testid={`document-item-${d.id}`}
                    className="flex items-center gap-3 rounded-2xl border border-[#E2EAE5] bg-white p-3"
                  >
                    <button onClick={() => navigate(`/documents/${d.id}/analysis`)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F0F4F1] text-[#2D5A4C]">
                        {isPdf ? <FileText size={18} /> : <ImageIcon size={18} />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-[#1A2E26]">{d.original_filename}</p>
                        <p className="text-[11px] text-[#7A9488]">{npages > 1 ? `${npages} ${t("xp.mpPagesShort")} · ` : ""}{fmtSize(d.size)}</p>
                      </div>
                    </button>
                    <DocDeleteButton doc={d} />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Button
          data-testid="doc-continue-create-case"
          variant="outline"
          onClick={() => navigate("/cases/new")}
          className="mt-8 h-11 w-full rounded-xl border-[#E2EAE5] text-sm font-semibold text-[#4A6358]"
        >
          {t("xp.manualEntry")}
        </Button>
      </main>
      <BottomNav />
    </div>
  );
}
